import React, { useEffect, useState } from 'react';
import UserConfirmation from 'js/components/route_utils/UserConfirmation';

type Props = {
  open: boolean;
  message?: string;
  title?: string;
  okButtonLabel?: string;
  onClose?: () => void;
  onConfirm: () => void;
};

const defaultMessage =
  'Are you sure you want to exit this editor?  All of your current changes will be discarded.';

/**
 * UserConfirmationForModals
 *
 * Use this component when you need to prompt users for confirmation before they exit or close a modal workflow,
 * especially when unsaved changes might be lost.
 * It is designed to be used in modal dialogs where accidental
 * navigation (such as pressing the browser back button or reloading the page) should trigger a confirmation dialog
 * to prevent data loss.
 */
export const UserConfirmationForModals = (props: Props) => {
  const { open, message = defaultMessage, onClose, onConfirm } = props;
  const [showExitModal, setShowExitModal] = useState(false);

  useEffect(() => {
    const handlePopState = handlePopStateWithConfirm(open, setShowExitModal);

    if (open) {
      window.addEventListener('beforeunload', dontReloadWithoutConfirmation);
      window.addEventListener('popstate', handlePopState);
      window.history.pushState(null, '', window.location.href);
    }

    return () => {
      window.removeEventListener('beforeunload', dontReloadWithoutConfirmation);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [open]);

  const handleConfirm = () => {
    onConfirm?.();
    if (showExitModal) {
      setShowExitModal(false);
    }
  };

  const handleClose = () => {
    onClose?.();
    if (showExitModal) {
      setShowExitModal(false);
    }
  };

  return (
    showExitModal && (
      <UserConfirmation message={message} onCancel={handleClose} onConfirm={handleConfirm} />
    )
  );
};

/**
 * This function is used to prevent the user from reloading the page without confirmation.
 */
function dontReloadWithoutConfirmation(e: BeforeUnloadEvent) {
  e.preventDefault();
}

function handlePopStateWithConfirm(open, setShowExitModal) {
  return (e: PopStateEvent) => {
    /**
     * 1. if the modal is open, prevent the default behavior of the browser
     * 2. set the showExitModal state to true
     * 3. push a new state to prevent the Router's confirmation from also triggering
     */
    if (open) {
      e.preventDefault();
      setShowExitModal(true);
      window.history.pushState(null, '', window.location.href);
    }
  };
}
